import { useEffect, useRef, useState } from "react";
import { Edit3, Settings, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { GoalCollectionEffortField } from "@/components/agent/details/GoalCollectionEffortField";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import { InsightForm, InsightManager } from "@/components/insights";
import { Button } from "@/components/ui/button";
import { FormDrawer } from "@/components/ui/FormDrawer";
import { Textarea } from "@/components/ui/textarea";
import { useEnablePhoneGoals } from "@/hooks/agent/useAgents";
import {
  useAttachInsight,
  useCreateInsightFromDescription,
  useDetachInsight,
  useInsights,
  useUpdateInsight,
} from "@/hooks/agent/useInsights";
import { Insight } from "@/services/api/InsightService";
import { Agent } from "@/types/agent/agent";
import { DEFAULT_BRANDING } from "@/types/ui/branding";

export const GenieInsights = ({ agent }: { agent: Agent }) => {
  const attachedInsight = agent?.insights || agent?.goal;
  const createInsightRef = useRef<HTMLDivElement>(null);
  const editInsightRef = useRef<HTMLDivElement>(null);
  const [showInsightManager, setShowInsightManager] = useState(false);
  const [showCreateInsight, setShowCreateInsight] = useState(false);
  const [showEditInsight, setShowEditInsight] = useState(false);
  const { data: fetchedInsights = [] } = useInsights(50, undefined, showInsightManager || !attachedInsight?.id);
  const createInsightFromDescription = useCreateInsightFromDescription();
  const updateInsight = useUpdateInsight();
  const attachInsight = useAttachInsight();
  const detachInsight = useDetachInsight();
  const enablePhoneGoals = useEnablePhoneGoals();
  const [selectedInsightId, setSelectedInsightId] = useState<number | null>(attachedInsight?.id || null);
  const [insightDescription, setInsightDescription] = useState("");
  const isAttaching = attachInsight.isPending;
  const isDetaching = detachInsight.isPending;
  const isLoading = isAttaching || isDetaching;

  const insights = attachedInsight
    ? [attachedInsight, ...fetchedInsights.filter((i) => i.id !== attachedInsight.id)]
    : fetchedInsights;
  const brand = agent.branding?.primaryColor || DEFAULT_BRANDING.primaryColor;
  const selectedFields = insights.find((insight) => insight.id === selectedInsightId)?.fields || [];
  const requiredFieldCount = selectedFields.filter((field) => field.required).length;
  // A Listener Genie records meetings, so it never asks anyone anything.
  const asksDuringConversations = agent.type !== "listener";

  const phoneGoalsOn =
    !!agent.settings?.platform_settings?.overrides?.enable_conversation_initiation_client_data_from_webhook;
  const offersPhoneGoals =
    !!selectedInsightId &&
    !!agent.phone &&
    asksDuringConversations &&
    !phoneGoalsOn &&
    agent.settings?.goal_collection_effort !== "listen_only";

  const handleEnablePhoneGoals = async () => {
    try {
      await enablePhoneGoals.mutateAsync(agent.id);
      toast.success("Done. This applies from the next conversation.");
    } catch (error) {
      console.error("Failed to turn on phone goals:", error);
      toast.error("Couldn't turn this on. Please try again.");
    }
  };

  useEffect(() => {
    if (attachedInsight?.id) {
      setSelectedInsightId(attachedInsight.id);
    }
  }, [attachedInsight?.id]);

  useEffect(() => {
    if (showCreateInsight) {
      createInsightRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [showCreateInsight]);

  useEffect(() => {
    if (showEditInsight) {
      editInsightRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [showEditInsight]);

  const handleUpdateInsight = async (updates: {
    name?: string;
    description?: string;
    fields?: any[];
    is_default?: boolean;
  }) => {
    if (!selectedInsightId) return;
    await updateInsight.mutateAsync({ id: selectedInsightId, updates });
    setShowEditInsight(false);
  };

  const handleInsightChange = async (insightId: string) => {
    const id = parseInt(insightId);
    if (isNaN(id) || id === selectedInsightId) return;

    setSelectedInsightId(id);

    if (agent?.id) {
      try {
        await attachInsight.mutateAsync({ agentId: agent.id, insightId: id });
      } catch (error) {
        console.error("Failed to attach insight:", error);
        setSelectedInsightId(attachedInsight?.id || null);
      }
    }
  };

  const handleRemoveInsight = async () => {
    setSelectedInsightId(null);

    if (agent?.id) {
      try {
        await detachInsight.mutateAsync(agent.id);
      } catch (error) {
        console.error("Failed to detach insight:", error);
        setSelectedInsightId(attachedInsight?.id || null);
      }
    }
  };

  const handleCreateInsight = async () => {
    if (!insightDescription.trim()) return;

    try {
      const newInsight = await createInsightFromDescription.mutateAsync({
        description: insightDescription,
        genieContext: {
          agentId: agent.id,
          name: agent.name,
          description: agent.description ?? undefined,
          purpose: agent.purpose ?? undefined,
          systemPrompt: agent.settings?.conversation_config?.agent?.prompt?.prompt ?? undefined,
        },
      });

      setSelectedInsightId(newInsight.id);

      if (agent?.id) {
        await attachInsight.mutateAsync({ agentId: agent.id, insightId: newInsight.id });
      }

      setShowCreateInsight(false);
      setInsightDescription("");
    } catch (error) {
      console.error("Failed to create insight:", error);
    }
  };

  return (
    <>
      <OverviewSectionCard
        title="Insights"
        subtitle="Define what information to extract from the conversation"
        genieIcon="lightbulb"
        brand={brand}
        secondaryColor={agent.branding?.secondaryColor}
        gradientEnabled={agent.branding?.gradientEnabled}
        gradientAngle={agent.branding?.gradientAngle}
      >
        <div className="space-y-4">
          {offersPhoneGoals && (
            <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
              <div className="rounded-[10px] bg-white p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#222B45]">Your genie isn't asking for these details on phone calls yet</p>
                  <p className="text-xs text-[#64748B] mt-0.5">
                    Turn this on and it will ask for them on phone calls too, the same way it does on your website.
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleEnablePhoneGoals}
                  disabled={enablePhoneGoals.isPending}
                  className="min-h-[44px] sm:min-h-0 flex-shrink-0 text-white font-semibold border-0 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] hover:brightness-105 transition-all"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {enablePhoneGoals.isPending ? "Turning on..." : "Turn on"}
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">Attached Insight</label>
            {selectedInsightId && insights.find((p) => p.id === selectedInsightId) ? (
              <div
                className={`w-full min-h-[44px] py-2 px-3 rounded-lg border border-gray-200 bg-gray-50 flex flex-col justify-center transition-opacity ${isLoading ? "opacity-60" : ""}`}
              >
                <span className="font-medium text-sm leading-tight">
                  {insights.find((p) => p.id === selectedInsightId)?.name}
                </span>
                {insights.find((p) => p.id === selectedInsightId)?.description && (
                  <span className="text-xs text-gray-500 leading-tight mt-0.5 line-clamp-2">
                    {insights.find((p) => p.id === selectedInsightId)?.description}
                  </span>
                )}
              </div>
            ) : (
              <div className="w-full min-h-[44px] py-2 px-3 rounded-lg border border-gray-200 bg-gray-50 flex items-center text-sm text-gray-400">
                {isLoading ? "Updating..." : "No insight attached — create or manage insights above"}
              </div>
            )}

            {selectedInsightId && insights.length > 0 && (
              <div className="border border-gray-200 rounded-lg bg-white">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">
                      {selectedFields.length} fields configured
                      {requiredFieldCount > 0 && `, ${requiredFieldCount} required`}
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5">
                      These fields will be extracted after each conversation
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowEditInsight((v) => !v)}
                      className="flex items-center gap-1.5"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                      Edit
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleRemoveInsight}
                      disabled={isDetaching}
                      className="text-red-600 hover:bg-red-50 border-red-200 transition-opacity"
                    >
                      {isDetaching ? "Removing..." : "Remove"}
                    </Button>
                  </div>
                </div>

                {!showEditInsight && (
                  <div className="p-4">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                      {selectedFields.map((field) => (
                        <div
                          key={field.key}
                          className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 font-medium flex items-center justify-between gap-2"
                        >
                          <span className="min-w-0 break-words">{field.label}</span>
                          {field.required && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold text-white flex-shrink-0 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
                              Required
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {showEditInsight && (
                  <div ref={editInsightRef} className="p-4">
                    <InsightForm
                      insight={insights.find((p) => p.id === selectedInsightId) as Insight | undefined}
                      genieContext={{
                        agentId: agent.id,
                        name: agent.name,
                        description: agent.description ?? undefined,
                        purpose: agent.purpose ?? undefined,
                        systemPrompt: agent.settings?.conversation_config?.agent?.prompt?.prompt ?? undefined,
                      }}
                      onSubmit={handleUpdateInsight}
                      onCancel={() => setShowEditInsight(false)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {selectedInsightId && asksDuringConversations && <GoalCollectionEffortField agent={agent} />}

          <div className="text-xs text-gray-400">
            Configure which information fields to extract from conversations to achieve your insights.
          </div>

          <div className="flex gap-2 justify-end">
            <Button
              type="button"
              size="sm"
              onClick={() => setShowCreateInsight((v) => !v)}
              disabled={isLoading}
              className="flex items-center gap-2 text-white shadow-sm hover:brightness-105 transition-opacity bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] border-0"
            >
              <Sparkles className="w-4 h-4" />
              Create Insight
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => setShowInsightManager(true)}
              disabled={isLoading}
              className="flex items-center gap-2 text-white shadow-sm hover:brightness-105 transition-opacity bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] border-0"
            >
              <Settings className="w-4 h-4" />
              Manage Insights
            </Button>
          </div>

          {showCreateInsight && (
            <div ref={createInsightRef} className="rounded-lg border border-gray-200 bg-white shadow-sm">
              <div className="p-4">
                <label className="block text-sm font-medium mb-2">Insight Description</label>
                <Textarea
                  value={insightDescription}
                  onChange={(e) => setInsightDescription(e.target.value)}
                  placeholder="e.g., Qualify contacts for our premium consulting services by capturing their budget, timeline, and specific needs"
                  rows={3}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Describe what you want to achieve with this insight. AI will generate the appropriate fields.
                </p>
                <div className="flex justify-end gap-2 mt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowCreateInsight(false);
                      setInsightDescription("");
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateInsight}
                    disabled={!insightDescription.trim() || createInsightFromDescription.isPending}
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    {createInsightFromDescription.isPending ? "Creating..." : "Create & Assign Insight"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </OverviewSectionCard>

      <FormDrawer
        isOpen={showInsightManager}
        onClose={() => setShowInsightManager(false)}
        title="Insight Manager"
        description="View, edit, and organise your insights. Select an insight to attach it to current genie, or clean up unassigned ones."
        width="min(100vw, 960px)"
      >
        <InsightManager
          onInsightSelect={(insight) => {
            handleInsightChange(insight.id.toString());
            setShowInsightManager(false);
          }}
          limit={50}
          agentName={agent.name}
        />
      </FormDrawer>
    </>
  );
};
